import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import classNames from 'classnames';
import _ from 'lodash';

import './BetLine.scss';
import { RootState } from '../../../../configs/store';
import { Bet } from '../../../../interfaces/BetslipState';
import formatOdds from '../../../../utils/formatOdds';
import Icon from '../../../Icon/Icon';
import CombinationsModal from 'src/components/NumbersBetting/LottoSummary/CombinationsModal/CombinationsModal';
import { toRoman } from 'src/utils/utils';
import BallsContainer from '../../NumberBet/Components/BallsContainer/BallsContainer';

interface BetLineProps {
  index: number,
  bet: Bet
  slip: 'single' | 'accumulator' | 'system',
  closeAll: boolean,
  myBets?: boolean
}

export default function BetLine({ index, bet, slip, closeAll, myBets=false }: BetLineProps) {
  const [expanded, setExpanded] = useState(true);
  const [showCombinations, setShowCombinations] = useState(false);
  const { selectedOddsType, selectedTimezone } = useSelector((state: RootState) => state.odds);
  const { divisor } = useSelector((state: RootState) => state.betslip);

  const { t } = useTranslation();
  const date = bet.startTime && utcToZonedTime(Number(bet.startTime) * 1000, selectedTimezone);

  useEffect(() => {
    setExpanded(closeAll);
  }, [closeAll]);

  const jsonLabel = bet.jsonLabel && typeof JSON.parse(bet.jsonLabel) === 'object' ? JSON.parse(bet.jsonLabel) : bet.jsonLabel || '';
  const name = bet.productType === 'horses' ? bet.meetingName : bet.tournament?.name ;

  return (
    <div
      className={
        classNames('bet-line', {
          [`bet-line--${ slip }`]: !!slip
        })
      }
    >
      { bet.productType === 'sports' &&  <div className="bet-line__icon sport-icon">{ bet.sportIcon }</div> }
      { bet.productType === 'numbers' && <div className="bet-line__icon--lotto" /> }

      <div
        className={
          classNames('bet-line__body', {
            'bet-line__body--my-bets': myBets,
            'bet-line__body--expanded': !expanded
          })
        }
      >
        <div className={
          classNames({
            'bet-line__market-info': true,
            'bet-line__market-info--my-bets': myBets
          })
        }
        >
          <div className="bet-line__main-container">
            { !_.isUndefined(index) && <span className="bet-line__line-number">{ index + 1 }</span> }
            <div className="bet-line__sub-container">
              <span className="bet-line__market">{ bet.marketDescription }:</span>
              {
                bet.productType === 'sports' && <span className={
                  classNames({
                    'bet-line__outcome': true,
                    'bet-line__outcome--my-bets': myBets
                  })
                }
                >&nbsp;{ bet.description }</span>
              }
              {
                bet.selectionType && (
                  <span className="betting-history__selection-type">{ bet.selectionType }</span>
                )
              }
              {
                (typeof jsonLabel !== 'object') &&
                <span className="betting-history__name">{ jsonLabel || bet?.name }</span>
              }
              {
                (bet.productType === 'horses' && (bet.prediction?.numbersToPredictCount > 1 || bet.oddsType === 'tote')) &&
                <div>
                  {
                    _.map(jsonLabel, (value, key) => {
                      if (value.length > 0){
                        return (
                          <>
                            <span key={ key } className="betting-history__name betting-history__name--no-padding">
                              {
                                (bet.prediction.type === 'cross_event' || isNaN(+key))
                                  ? `${ key } -`
                                  : `${ toRoman(key) } -`
                              }
                            </span>
                            <span className="betting-history__position">{ value.join(',') }</span>
                          </>
                        );
                      }

                      return null;
                    })
                  }
                </div>
              }
              {
                (bet.productType === 'horses') && ((bet.prediction?.numbersToPredictCount === 1 || !bet.prediction) && bet.oddsType !== 'tote') &&
            <>
              <span className={
                classNames({
                  'betting-history__position' : bet.prediction && bet.prediction.numbersToPredictCount > 1,
                  'betting-history__name': !bet.prediction || bet.prediction?.numbersToPredictCount === 1
                })
              }
              >{ !_.isEmpty(jsonLabel) ? `${ jsonLabel.id }. ${ jsonLabel.competitor }` : bet.prediction.anyNumbers.join(',') }</span>
            </>
              }
            </div>
          </div>
          <div
            className={
              classNames('bet-line__odds', {
                'bet-line__odds--arrow': true,
                'bet-line__odds--my-bets': myBets
              })
            }
          >
            { bet.odds && formatOdds(bet.odds, selectedOddsType) }
          </div>

          <Icon icon="arrow" className="bet-line__expand" onClick={ () => setExpanded(!expanded) } />
        </div>

        <div className="bet-line__event-info">
          <div className="bet-line__event">{ bet.eventName }</div>
          {
            expanded &&
              <div className="bet-line__event-details">
                {
                  date && (
                    <>
                      { format(date, 'dd/MM') }
                      &nbsp;|&nbsp;
                      { format(date, 'HH:mm') }
                      &nbsp;|&nbsp;
                    </>
                  )
                }
                <span>{ bet.venue }</span>
                <div className="bet-line__tournament-details">
                  <span className="bet-line__tournament-name">{ `${ name }, ${ bet.tournamentGroup?.name }` }</span>
                </div>
              </div>
          }
        </div>
      </div>
      {
        bet.productType === 'numbers' && bet.prediction &&
        <div className="numberbet-details__balls-container">
          <BallsContainer anyNumbers={ jsonLabel } sameBallset={ bet.sameBallSet } />
        </div>
      }
      {
        bet.productType === 'numbers' && bet.prediction && bet.prediction.permutationsCount > 1 &&
          <div className="bet-line__combinations-wrapper">
            {
              bet.prediction.permutationsCount > 1 && (
                <>
                  <span
                    className="bet-line__combinations"
                    onClick={ () => setShowCombinations(true) }
                  >
                    { t('betslip.combinations') }
                  </span>
                  <span className="bet-line__combinations bet-line__combinations--result">
                    { bet.prediction.permutationsCount }
                  </span>
                </>
              )
            }
          </div>
      }
      {
        showCombinations && (
          <>
            <CombinationsModal
              title={ t('numbers.combinations') }
              permutations={ bet.prediction.permutations }
              stake={ bet.prediction.singleStake / divisor }
              profit={ bet.prediction.minimumReturn / divisor }
              odds={ bet.odds }
              onClick={ () => setShowCombinations(!showCombinations) }
            />
          </>
        )
      }
    </div>
  );
}
